import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useVendorApplications } from '../hooks/useVendorApplications';
import { toast } from 'react-hot-toast';

const VendorApplications = () => {
  const {
    applications,
    loading,
    error,
    page,
    totalPages,
    itemsPerPage,
    fetchApplications,
    approveApplication,
    rejectApplication,
    changePage,
    clearError,
    resetToFirstPage
  } = useVendorApplications();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Fetch applications when filters change
  useEffect(() => {
    fetchApplications({ search: searchTerm, status: statusFilter });
  }, [fetchApplications, searchTerm, statusFilter]);

  // Initial fetch
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Handle application action (approve/reject)
  const handleApplicationAction = async () => {
    if (!selectedApplication) return;

    try {
      setProcessingAction(true);
      
      let result;
      if (actionType === 'approve') {
        result = await approveApplication(selectedApplication._id);
      } else if (actionType === 'reject') {
        result = await rejectApplication(selectedApplication._id, { reason: rejectionReason });
      }

      if (result.success) {
        // Refresh the applications list
        fetchApplications({ search: searchTerm, status: statusFilter });
        
        // Close dialogs
        setActionDialogOpen(false);
        setDetailDialogOpen(false);
        setSelectedApplication(null);
        setRejectionReason('');
        setActionType('');
      }
    } catch (error) {
      console.error('Error processing action:', error);
    } finally {
      setProcessingAction(false);
    }
  };

  // Open action dialog
  const openActionDialog = (application, type) => {
    setSelectedApplication(application);
    setActionType(type);
    setActionDialogOpen(true);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending Review';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    resetToFirstPage();
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    resetToFirstPage();
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    changePage(value);
  };

  // Filtered applications
  const filteredApplications = (() => {
    let filtered = applications;

    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.userId?.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.businessCategory?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    return filtered;
  })();

  if (loading && applications.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && applications.length === 0) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => fetchApplications()}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Vendor Applications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and review vendor applications for the platform
        </Typography>
      </Box>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by business name, user name, or email..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status Filter"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchApplications({ search: searchTerm, status: statusFilter })}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Showing: {filteredApplications.length} of {applications.length} applications
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Applications Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            zIndex={1}
          >
            <CircularProgress />
          </Box>
        )}
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>Applicant</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applied Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {applications.length === 0 
                        ? 'No applications found' 
                        : 'No applications match the current filters'
                      }
                    </Typography>
                    {applications.length > 0 && (
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          resetToFirstPage();
                        }}
                        sx={{ mt: 1 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.map((application) => (
                  <TableRow key={application._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon color="primary" />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {application.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {application.businessDescription?.substring(0, 50)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={application.userId?.profilePic} sx={{ width: 32, height: 32 }}>
                          {application.userId?.fullname?.[0] || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {application.userId?.fullname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {application.userId?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={application.businessCategory} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(application.status)}
                        color={getStatusColor(application.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(application.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedApplication(application);
                              setDetailDialogOpen(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {application.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openActionDialog(application, 'approve')}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openActionDialog(application, 'reject')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Application Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Application Details</Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Grid container spacing={3}>
                {/* Business Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Business Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <BusinessIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Business Name"
                            secondary={selectedApplication.businessName}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Category"
                            secondary={selectedApplication.businessCategory}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Description"
                            secondary={selectedApplication.businessDescription}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <PhoneIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Contact Phone"
                            secondary={selectedApplication.contactPhone}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <EmailIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Contact Email"
                            secondary={selectedApplication.contactEmail}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Applicant Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Applicant Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={selectedApplication.userId?.profilePic}>
                              {selectedApplication.userId?.fullname?.[0] || 'U'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary="Full Name"
                            secondary={selectedApplication.userId?.fullname}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <EmailIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Email"
                            secondary={selectedApplication.userId?.email}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <PhoneIcon color="primary" />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Phone"
                            secondary={selectedApplication.userId?.phone}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Documents and Additional Info */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Documents & Additional Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Business License
                          </Typography>
                          <Typography variant="body2">
                            {selectedApplication.businessLicense || 'Not provided'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Tax ID
                          </Typography>
                          <Typography variant="body2">
                            {selectedApplication.taxId || 'Not provided'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Application Date
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(selectedApplication.createdAt)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Current Status
                          </Typography>
                          <Chip
                            label={getStatusText(selectedApplication.status)}
                            color={getStatusColor(selectedApplication.status)}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedApplication?.status === 'pending' && (
            <>
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  openActionDialog(selectedApplication, 'approve');
                }}
              >
                Approve
              </Button>
              <Button
                color="error"
                variant="contained"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setDetailDialogOpen(false);
                  openActionDialog(selectedApplication, 'reject');
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Application' : 'Reject Application'}
        </DialogTitle>
        <DialogContent>
          {actionType === 'approve' ? (
            <DialogContentText>
              Are you sure you want to approve this vendor application? 
              The user will be able to create events and sell tickets once approved.
            </DialogContentText>
          ) : (
            <Box>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to reject this vendor application? 
                Please provide a reason for rejection.
              </DialogContentText>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApplicationAction}
            color={actionType === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={processingAction || (actionType === 'reject' && !rejectionReason.trim())}
          >
            {processingAction ? (
              <CircularProgress size={20} />
            ) : actionType === 'approve' ? (
              'Approve'
            ) : (
              'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorApplications; 